import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./AllRequestsChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsOverviewService from "../RequestsOverviewService";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardLegendTop,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    createMonitorDashboardLegendHeightPlugin,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
  diProvide: [RequestsOverviewService],
})
export default class AllRequestsChart extends Vue {
  @Inject(RequestsOverviewService)
  public requestsOverviewService!: RequestsOverviewService;

  @Prop({ required: true }) public filter!: any;

  private chart: any = null;
  private canvasId: string = "all-requests-chart-canvas";

  public hasData: boolean = true;
  public isLoading: boolean = false;
  public totalCount: number = 0;
  private requestVersion: number = 0;

  private get statuses(): any[] {
    return [
      { id: 1, label: String(this.$t("monitorDashboard.pendingApproval")) },
      { id: 2, label: String(this.$t("monitorDashboard.approved")) },
      { id: 3, label: String(this.$t("monitorDashboard.rejected")) },
      { id: 4, label: String(this.$t("monitorDashboard.paid")) },
      { id: 5, label: String(this.$t("monitorDashboard.expired")) },
      { id: 6, label: String(this.$t("monitorDashboard.deleted")) },
      { id: 11, label: String(this.$t("monitorDashboard.statusDeactivated")) },
    ];
  }

  private get customerTypes(): any[] {
    return [
      { id: 1, label: String(this.$t("monitorDashboard.government")) },
      { id: 3, label: String(this.$t("monitorDashboard.private")) },
      { id: 2, label: String(this.$t("monitorDashboard.charity")) },
    ];
  }

  public async refresh(): Promise<void> {
    if (!this.filter) return;

    this.isLoading = true;
    const currentRequestVersion = ++this.requestVersion;

    const payload = {
      dateFrom: this.filter.dateFrom,
      dateTo: this.filter.dateTo,
      tenantId: this.filter.tenant,
      providerId: this.filter.providerId,
      customerType: this.filter.customerType,
      requestType: this.filter.requestType,
    };

    const resp = await this.requestsOverviewService.GetAllRequestsChart(payload);
    if (currentRequestVersion !== this.requestVersion) {
      return;
    }

    this.isLoading = false;

    if (!resp || !resp.result || !Array.isArray(resp.result)) {
      this.hasData = false;
      this.totalCount = 0;
      this.destroyChart();
      return;
    }

    this.totalCount = resp.totalCount ? Number(resp.totalCount) : 0;

    await this.$nextTick();
    this.renderChart(resp.result);
  }

  public onTitleClick(): void {
    this.navigateToAllRequestsDetails();
  }

  private navigateToAllRequestsDetails(): void {
    const f = this.filter;
    const overviewPayload = {
      dateFrom: f.dateFrom,
      dateTo: f.dateTo,
      tenantId: f.tenant,
      providerId: f.providerId,
      customerType: f.customerType || 0,
      requestType: f.requestType || 0,
    };

    try {
      sessionStorage.setItem(
        "monitorDashboard.overviewFilter",
        JSON.stringify(overviewPayload),
      );
    } catch (e) {
    }

    this.$router.push({ name: "AllRequestsDetails" });
  }

  private onChartClick(): void {
    this.navigateToAllRequestsDetails();
  }

  public beforeDestroy(): void {
    this.destroyChart();
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private renderChart(rows: any[]): void {
    const map: any = {};
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const key = String(r.requestStatus) + ":" + String(r.customerType);
      map[key] = Number(r.count);
    }

    const labels: string[] = [];
    for (let i = 0; i < this.statuses.length; i++) {
      labels.push(this.statuses[i].label);
    }

    const datasets: any[] = [];
    let anyNonZero = false;
    let total = 0;

    for (let j = 0; j < this.customerTypes.length; j++) {
      const ct = this.customerTypes[j];
      const data: number[] = [];

      for (let i = 0; i < this.statuses.length; i++) {
        const st = this.statuses[i];
        const k = String(st.id) + ":" + String(ct.id);
        const v = map.hasOwnProperty(k) ? Number(map[k]) : 0;

        data.push(v);

        if (v !== 0) anyNonZero = true;
        total += v;
      }

      const fill = getMonitorDashboardChartColor("allRequests", String(ct.id));
      datasets.push({
        label: ct.label,
        data: data,
        backgroundColor: fill,
        borderColor: monitorDashboardDarkerBorder(fill),
        borderWidth: 2,
        categoryPercentage: 0.7,
        barPercentage: 0.85,
      });
    }

    this.hasData = anyNonZero;
    const yAxisMax = this.getYAxisMax(datasets);

    this.destroyChart();
    if (!anyNonZero) return;

    const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
    if (!el) return;

    const ctx = el.getContext("2d") as any;
    const self = this;
    const fontFamily = getMonitorDashboardFontFamily();
    const baseOpts = buildMonitorDashboardResponsiveChartOptions();

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: datasets,
      },
      plugins: [
        createMonitorDashboardLegendHeightPlugin(14),
        {
          afterDatasetsDraw: function (chart: any) {
            self.drawZeroValueMarkers(chart);
            self.drawValueLabels(chart);
          },
        },
      ],
      options: Object.assign({}, baseOpts, {
        onClick: function () {
          self.onChartClick();
        },
        legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
        scales: {
          xAxes: [
            {
              stacked: false,
              gridLines: { display: false },
              ticks: buildMonitorDashboardCategoryAxisTicks(14, fontFamily),
            },
          ],
          yAxes: [
            {
              stacked: false,
              ticks: buildMonitorDashboardValueAxisTicks(12, fontFamily, true, yAxisMax, this.getYAxisStep(yAxisMax)),
            },
          ],
        },
        tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
          mode: "index",
          intersect: false,
          callbacks: {
            title: function (tooltipItems: any[]) {
              if (!tooltipItems || tooltipItems.length === 0) return "";
              const idx = tooltipItems[0].index;
              return labels[idx];
            },
            label: function (tooltipItem: any, data: any) {
              const dsIndex = tooltipItem.datasetIndex;
              const idx = tooltipItem.index;
              const ds = data && data.datasets && data.datasets[dsIndex] ? data.datasets[dsIndex] : null;
              const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
              const value = isNaN(raw) ? 0 : raw;
              return String(ds && ds.label ? ds.label : "") + ": " + String(value);
            },
            afterBody: function (items: any[], data: any) {
              let statusTotal = 0;
              if (items && items.length) {
                for (let i = 0; i < items.length; i++) {
                  const item = items[i];
                  const ds = data && data.datasets && data.datasets[item.datasetIndex]
                    ? data.datasets[item.datasetIndex]
                    : null;
                  const raw = ds && ds.data && ds.data.length > item.index ? Number(ds.data[item.index]) : 0;
                  const value = isNaN(raw) ? 0 : raw;
                  statusTotal += value;
                }
              }
              return String(self.$t("monitorDashboard.total")) + ": " + String(statusTotal);
            },
          },
        }),
      }),
    });
  }

  public drawValueLabels(chart: any): void {
    const ctx2: any = chart.ctx;

    ctx2.save();
    ctx2.textAlign = "center";
    ctx2.textBaseline = "bottom";
    ctx2.font = "10px " + getMonitorDashboardFontFamily();
    ctx2.fillStyle = "#6b7280";

    for (let di = 0; di < chart.data.datasets.length; di++) {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) continue;

      for (let i = 0; i < meta.data.length; i++) {
        const val = Number(chart.data.datasets[di].data[i]);
        const bar = meta.data[i];
        const x = bar._model.x;
        const yTop = bar._model.y;

        if (val === 0) {
          const yAxis = chart.scales["y-axis-0"];
          const yZero = yAxis ? yAxis.getPixelForValue(0) : yTop;
          ctx2.fillText("0", x, yZero - 6);
          continue;
        }

        ctx2.fillText(String(val), x, yTop - 6);
      }
    }

    ctx2.restore();
  }

  private drawZeroValueMarkers(chart: any): void {
    const ctx2: any = chart.ctx;
    const yAxis = chart.scales["y-axis-0"];
    if (!yAxis) return;

    const yZero = yAxis.getPixelForValue(0);

    ctx2.save();
    ctx2.lineWidth = 3;

    for (let di = 0; di < chart.data.datasets.length; di++) {
      const meta = chart.getDatasetMeta(di);
      if (!meta || meta.hidden) continue;

      const strokeColor = chart.data.datasets[di].borderColor;

      for (let i = 0; i < meta.data.length; i++) {
        const val = Number(chart.data.datasets[di].data[i]);
        if (val !== 0) continue;

        const bar = meta.data[i];
        const model = bar._model;
        const x = model.x;
        const halfWidth = Math.max(1, (model.width || 0) / 2);

        ctx2.strokeStyle = Array.isArray(strokeColor) ? strokeColor[i] || strokeColor[0] : strokeColor;
        ctx2.beginPath();
        ctx2.moveTo(x - halfWidth, yZero);
        ctx2.lineTo(x + halfWidth, yZero);
        ctx2.stroke();
      }
    }

    ctx2.restore();
  }

  private getYAxisStep(maxVal: number): number {
    const targetTicks = 18;
    const rawStep = Math.ceil(maxVal / targetTicks);
    return this.getNiceStep(rawStep);
  }

  private getNiceStep(rawStep: number): number {
    if (rawStep <= 1) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;
    if (normalized <= 1) return 1 * magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
  }

  private getYAxisMax(datasets: any[]): number {
    let maxVal = 0;
    for (let i = 0; i < datasets.length; i++) {
      const data = datasets[i].data;
      for (let j = 0; j < data.length; j++) {
        const v = Number(data[j] || 0);
        if (v > maxVal) maxVal = v;
      }
    }
    if (maxVal <= 0) return 10;
    const withPadding = Math.ceil(maxVal * 1.03);
    const step = this.getYAxisStep(withPadding);
    return Math.ceil(withPadding / step) * step;
  }
}
